import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Layout } from "@/components/Layout";
import { GameRequestButton } from "@/components/GameRequestButton";
import { GameLikeButton } from "@/components/GameLikeButton";
import { GameFavoriteButton } from "@/components/GameFavoriteButton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { parseGenres } from "@/data/gameGenres";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Filter,
  Search,
  Calendar,
  User,
  Tag,
  SortAsc,
  SortDesc,
  ChevronDown
} from "lucide-react";
import { GamePriceStatus } from "@/components/GamePriceStatus";

interface Game {
  id: string;
  title: string;
  description: string;
  genres: string[];
  cover_image_url: string | null;
  release_date: string;
  steam_url: string | null;
  itch_url: string | null;
  available_keys: number;
  allow_requests: boolean;
  developer: {
    id: string;
    display_name: string;
    studio_name?: string;
    studio_slug?: string;
    country?: string | null;
  };
}

interface FilterState {
  year: string;
  developer: string;
  genre: string;
  country: string;
  search: string;
}

type SortOption = 'name' | 'release_desc' | 'release_asc';

export default function Games() {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('release_desc');
  const [filters, setFilters] = useState<FilterState>({
    year: 'all',
    developer: 'all',
    genre: 'all',
    country: 'all',
    search: ''
  });

  // Request Dialog State
  const [isRequestingKey, setIsRequestingKey] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [selectedGameForRequest, setSelectedGameForRequest] = useState<Game | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20); // Change this value to control how many games are displayed per page
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableDevelopers, setAvailableDevelopers] = useState<{ id: string, name: string }[]>([]);
  const [availableGenres, setAvailableGenres] = useState<string[]>([]);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);

  useEffect(() => {
    fetchGames();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [games, filters, sortBy]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("games")
        .select(`
          *,
          developer:profiles!games_developer_id_fkey(
            id,
            display_name,
            studio_name,
            studio_slug,
            country
          )
        `)
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching games:", error);
        toast.error("Failed to load games");
        return;
      }

      const processedGames = (data || []).map(game => ({
        ...(game as any),
        release_date: (game as any).release_date || (game as any).created_at || new Date().toISOString(),
        allow_requests: (game as any).allow_requests !== false,
        genres: parseGenres((game as any).genre || ""),
      })) as Game[];

      setGames(processedGames);

      // Extract filter options
      const years = [...new Set(processedGames.map(game =>
        new Date(game.release_date).getFullYear().toString()
      ))].sort((a, b) => b.localeCompare(a));

      const developers = processedGames
        .map(game => ({
          id: game.developer.id,
          name: game.developer.studio_name || game.developer.display_name
        }))
        .filter((dev, index, self) =>
          index === self.findIndex(d => d.id === dev.id)
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      const allGenres = [...new Set(processedGames.flatMap(game => game.genres))].sort();

      const countries = [...new Set(processedGames
        .map(game => game.developer.country)
        .filter(country => country) // Filter out null/undefined
      )] as string[];
      countries.sort();

      setAvailableYears(years);
      setAvailableDevelopers(developers);
      setAvailableGenres(allGenres);
      setAvailableCountries(countries);
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...games];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(game =>
        game.title.toLowerCase().includes(searchLower) ||
        game.developer.display_name.toLowerCase().includes(searchLower) ||
        (game.developer.studio_name && game.developer.studio_name.toLowerCase().includes(searchLower))
      );
    }

    // Apply year filter
    if (filters.year && filters.year !== 'all') {
      filtered = filtered.filter(game =>
        new Date(game.release_date).getFullYear().toString() === filters.year
      );
    }

    // Apply developer filter
    if (filters.developer && filters.developer !== 'all') {
      filtered = filtered.filter(game => game.developer.id === filters.developer);
    }

    // Apply genre filter
    if (filters.genre && filters.genre !== 'all') {
      filtered = filtered.filter(game => game.genres.includes(filters.genre));
    }

    // Apply country filter
    if (filters.country && filters.country !== 'all') {
      filtered = filtered.filter(game => game.developer.country === filters.country);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.title.localeCompare(b.title);
        case 'release_desc':
          return new Date(b.release_date).getTime() - new Date(a.release_date).getTime();
        case 'release_asc':
          return new Date(a.release_date).getTime() - new Date(b.release_date).getTime();
        default:
          return 0;
      }
    });

    setFilteredGames(filtered);
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredGames.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentGames = filteredGames.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      year: 'all',
      developer: 'all',
      genre: 'all',
      country: 'all',
      search: ''
    });
  };

  const handleViewGameDetail = (game: Game) => {
    navigate(`/game/${game.id}`);
  };

  const openRequestDialog = (game: Game) => {
    setSelectedGameForRequest(game);
    setIsRequestingKey(true);
  };

  const handleRequestKey = async () => {
    if (!user || !selectedGameForRequest) return;

    setIsSubmittingRequest(true);
    try {
      const { error } = await supabase
        .from("key_requests")
        .insert({
          game_id: selectedGameForRequest.id,
          creator_id: user.id,
          developer_id: selectedGameForRequest.developer.id,
          message: requestMessage.trim() || null
        });

      if (error) {
        if (error.code === "23505") {
          toast.error("You've already requested a key for this game");
        } else {
          toast.error(error.message);
        }
      } else {
        toast.success("Key request submitted successfully!");
        setIsRequestingKey(false);
        setRequestMessage("");
        setSelectedGameForRequest(null);
      }
    } catch (error) {
      console.error('Error requesting key:', error);
      toast.error("Error submitting key request");
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const getSortLabel = (sort: SortOption) => {
    switch (sort) {
      case 'name': return 'Name';
      case 'release_desc': return 'Release Date (Recent)';
      case 'release_asc': return 'Release Date (Oldest)';
      default: return 'Sort by';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[calc(100vh-120px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title="Browse Games"
        description="Discover and request keys for the best indie games on IndieKeys."
      />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            {/* Mobile Filter Button */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden w-full mb-4">
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-6">
                  <FilterContent
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={clearFilters}
                    availableYears={availableYears}
                    availableDevelopers={availableDevelopers}
                    availableGenres={availableGenres}
                    availableCountries={availableCountries}
                  />
                </div>
              </SheetContent>
            </Sheet>

            {/* Desktop Filters */}
            <div className="hidden lg:block">
              <FilterContent
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                availableYears={availableYears}
                availableDevelopers={availableDevelopers}
                availableGenres={availableGenres}
                availableCountries={availableCountries}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold">All Games</h1>
                <p className="text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredGames.length)} of {filteredGames.length} game{filteredGames.length !== 1 ? 's' : ''} found
                </p>
              </div>

              <div className="flex items-center gap-4">
                {/* Calendar Button */}
                <Button
                  variant="outline"
                  onClick={() => navigate('/calendar')}
                  className="flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Calendar
                </Button>

                {/* Sort */}
                <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="release_desc">Release Date (Recent)</SelectItem>
                    <SelectItem value="release_asc">Release Date (Oldest)</SelectItem>
                  </SelectContent>
                </Select>

              </div>
            </div>

            {/* Games List */}
            {filteredGames.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No games found with these filters.</p>
                <Button variant="outline" onClick={clearFilters} className="mt-4">
                  Clear filters
                </Button>
              </Card>
            ) : (
              <>
                <div className="space-y-4">
                  {currentGames.map((game) => (
                    <GameCard
                      key={game.id}
                      game={game}
                      onViewDetail={handleViewGameDetail}
                      onRequestClick={openRequestDialog}
                      userType={userType}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>

                    <div className="flex gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => goToPage(page)}
                          className="w-10"
                        >
                          {page}
                        </Button>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Request Key Dialog */}
        <Dialog open={isRequestingKey} onOpenChange={setIsRequestingKey}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Key for {selectedGameForRequest?.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="request-message">Message (optional)</Label>
                <Textarea
                  id="request-message"
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Tell the developer why you'd like a key for this game..."
                  rows={4}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleRequestKey}
                  disabled={isSubmittingRequest}
                  className="flex-1"
                >
                  {isSubmittingRequest ? "Submitting..." : "Submit Request"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsRequestingKey(false);
                    setRequestMessage("");
                    setSelectedGameForRequest(null);
                  }}
                  disabled={isSubmittingRequest}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// Filter Content Component
interface FilterContentProps {
  filters: FilterState;
  onFilterChange: (key: keyof FilterState, value: string) => void;
  onClearFilters: () => void;
  availableYears: string[];
  availableDevelopers: { id: string, name: string }[];
  availableGenres: string[];
  availableCountries: string[];
}

const FilterContent = ({
  filters,
  onFilterChange,
  onClearFilters,
  availableYears,
  availableDevelopers,
  availableGenres,
  availableCountries
}: FilterContentProps) => (
  <div className="space-y-6">
    {/* Search */}
    <div>
      <Label htmlFor="search">Search</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          id="search"
          placeholder="Game name or developer..."
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          className="pl-10"
        />
      </div>
    </div>

    {/* Year Filter */}
    <div>
      <Label htmlFor="year">Release Year</Label>
      <Select value={filters.year} onValueChange={(value) => onFilterChange('year', value)}>
        <SelectTrigger>
          <SelectValue placeholder="All years" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All years</SelectItem>
          {availableYears.map(year => (
            <SelectItem key={year} value={year}>{year}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Developer Filter */}
    <div>
      <Label htmlFor="developer">Developer</Label>
      <Select value={filters.developer} onValueChange={(value) => onFilterChange('developer', value)}>
        <SelectTrigger>
          <SelectValue placeholder="All developers" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All developers</SelectItem>
          {availableDevelopers.map(dev => (
            <SelectItem key={dev.id} value={dev.id}>{dev.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Genre Filter */}
    <div>
      <Label htmlFor="genre">Genre</Label>
      <Select value={filters.genre} onValueChange={(value) => onFilterChange('genre', value)}>
        <SelectTrigger>
          <SelectValue placeholder="All genres" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All genres</SelectItem>
          {availableGenres.map(genre => (
            <SelectItem key={genre} value={genre}>{genre}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Country Filter */}
    <div>
      <Label htmlFor="country">Country</Label>
      <Select value={filters.country} onValueChange={(value) => onFilterChange('country', value)}>
        <SelectTrigger>
          <SelectValue placeholder="All countries" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All countries</SelectItem>
          {availableCountries.map(country => (
            <SelectItem key={country} value={country}>{country}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Clear Filters */}
    <Button variant="outline" onClick={onClearFilters} className="w-full">
      Clear filters
    </Button>
  </div>
);

// Game Card Component
interface GameCardProps {
  game: Game;
  onViewDetail: (game: Game) => void;
  onRequestClick: (game: Game) => void;
  userType: string | null;
}

const GameCard = ({ game, onViewDetail, onRequestClick, userType }: GameCardProps) => {
  return (
    <Card className="p-4 hover:bg-muted/50 transition-colors rounded-custom-paddings">
      <div className="flex flex-col sm:flex-row gap-0 sm:gap-4">
        <img
          src={game.cover_image_url || '/placeholder.svg'}
          alt={game.title}
          className="w-56 h-32 object-cover rounded-custom-paddings p-0 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => onViewDetail(game)}
        />
        <div className="flex-1 min-w-0 pr-3 py-2 pl-3 sm:pl-0">
          <div className="flex items-center justify-between">
            <h3
              className="text-lg font-semibold mb-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => onViewDetail(game)}
            >
              {game.title}
            </h3>
            {userType === 'player' && <GamePriceStatus gameId={game.id} price={game.price || 0} />}
          </div>
          <div className="flex flex-wrap gap-1 mb-3">
            {game.genres.slice(0, 5).map((genre, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {genre}
              </Badge>
            ))}
            {game.genres.length > 5 && (
              <Badge variant="outline" className="text-xs">
                +{game.genres.length - 5}
              </Badge>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {new Date(game.release_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <div className="flex gap-2">
              {userType === 'player' ? (
                <div className="flex items-center gap-2">
                  <GameLikeButton gameId={game.id} />
                  <GameFavoriteButton gameId={game.id} />
                </div>
              ) : (
                <>
                  {/* <GameRequestButton
                  gameId={game.id}
                  gameTitle={game.title}
                  allowRequests={game.allow_requests}
                  availableKeys={game.available_keys}
                  onRequestClick={() => onRequestClick(game)}
                  className="text-sm py-1"
                /> */}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
